import { GeoJSONSource, type Map as MlMap, type ExpressionSpecification } from 'maplibre-gl'
import type { Alert, Cell } from '../../lib/types'
import { isOnSiteCell, TIER_COLOR } from '../../lib/format'

/**
 * Two heat layers, deliberately drawn differently.
 *
 * **Congestion** is a measurement over a known geometry: an H3 res-9 cell with an
 * avg speed and a learned free-flow baseline. It gets discrete cell markers sized by
 * vehicle count and coloured by score, because the cell boundary is real and blurring it
 * would invent precision the data does not have. A cell whose baseline is still
 * provisional renders hollow -- "we don't know this road yet" is not "this road is clear",
 * and a filled green hexagon would say the second thing.
 *
 * **Events** are point occurrences with no geometry at all: harsh braking, overspeed,
 * unscheduled stops. Density is the whole question, so they get a real heatmap kernel.
 * Nothing about an individual event location is precise enough to deserve a marker.
 *
 * Events split into three independently-coloured heatmaps, not one. A single blended
 * density kernel answers "where is it busy" but not "busy with what" -- a cluster of
 * harsh braking and a cluster of stale-feed dropouts are unrelated problems (one is a
 * driver, the other is a device), and painting them the same colour erased that
 * distinction. Each category gets its own hue so they can overlap on the map without
 * merging into one meaning.
 */

export const SRC_CELLS = 'cong-cells'
export const SRC_EVENTS = 'event-points'

export const LYR_CELL_HEAT = 'cong-heat'
export const LYR_CELL_DOT = 'cong-dot'
export const LYR_CELL_LABEL = 'cong-label'

const empty: GeoJSON.FeatureCollection<GeoJSON.Point> = { type: 'FeatureCollection', features: [] }

export type HeatMode = 'off' | 'congestion' | 'events'

export type EventCategory = 'driver' | 'route'

export interface EventCategoryDef {
  id: EventCategory
  label: string
  /** Base hue the density kernel ramps up to -- see EVENT_HEAT_COLOR below. */
  color: string
  kinds: readonly string[]
}

/**
 * Event kinds worth a heatmap, grouped by what kind of problem they actually are:
 *  - driver: the person behind the wheel did something risky.
 *  - route: the vehicle left its corridor or stopped where it should not have.
 *
 * There is deliberately no "data quality" category (GPS_JUMP, STALE_FEED): a feed that
 * misbehaves is a device problem, not a place on the map, so it does not belong on a
 * density heatmap. Those kinds stay in the alert tape and the fleet cards.
 */
export const EVENT_CATEGORIES: readonly EventCategoryDef[] = [
  { id: 'driver', label: 'Driver behaviour', color: '#F04B4B',
    kinds: ['HARSH_BRAKE', 'HARSH_BRAKING', 'HARSH_ACCEL', 'OVERSPEED'] },
  { id: 'route', label: 'Route diversion', color: '#A855F7',
    kinds: ['UNSCHEDULED_STOP', 'DIVERSION'] },
]

const CATEGORY_OF_KIND = new Map<string, EventCategory>(
  EVENT_CATEGORIES.flatMap((c) => c.kinds.map((k) => [k, c.id] as const)),
)

/** Event kinds worth a heatmap: things that happen *at a place* for a reason. */
export const EVENT_KINDS = new Set(CATEGORY_OF_KIND.keys())

export function eventCategoryOf(kind: string): EventCategory | undefined {
  return CATEGORY_OF_KIND.get(kind)
}

export function eventLayerId(category: EventCategory): string {
  return `event-heat-${category}`
}

export function cellFeatures(cells: readonly Cell[]): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: 'FeatureCollection',
    features: cells
      .filter((c) => Number.isFinite(c.centroid_lat) && Number.isFinite(c.centroid_lon))
      .map((c) => ({
        type: 'Feature',
        id: c.cell,
        geometry: { type: 'Point', coordinates: [c.centroid_lon, c.centroid_lat] },
        properties: {
          cell: c.cell,
          score: c.score ?? 0,
          avg_speed_kph: c.avg_speed_kph,
          free_flow_kph: c.free_flow_kph,
          vehicles: c.distinct_vehicles ?? 0,
          stopped_ratio: c.stopped_ratio ?? 0,
          provisional: c.baseline_provisional ? 1 : 0,
          // Plant/depot/yard/gate/queue dwell scores near-identically to a real jam
          // (near-zero speed, high stopped_ratio) -- the backend scores it so the
          // heatmap CAN show real on-site slowness, but never alerts on it. DEGRADED
          // below excludes it from the "is the corridor moving" layer for the same
          // reason -- see isOnSiteCell's own comment.
          on_site: isOnSiteCell(c.fence_types) ? 1 : 0,
        },
      })),
  }
}

/**
 * Alerts that carry a position become heat points, weighted by severity. Alerts without
 * coordinates are dropped rather than placed at their vehicle's *current* position --
 * that would smear a brake event onto wherever the bus happens to be now.
 */
export function eventFeatures(alerts: readonly Alert[]): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: 'FeatureCollection',
    features: alerts
      .filter((a) => a.lat != null && a.lon != null && EVENT_KINDS.has(a.kind))
      .map((a) => ({
        type: 'Feature',
        id: a.alert_id,
        geometry: { type: 'Point', coordinates: [a.lon as number, a.lat as number] },
        properties: {
          kind: a.kind,
          // Safe: the filter above already requires a.kind to be an EVENT_KINDS member,
          // and EVENT_KINDS is exactly CATEGORY_OF_KIND's key set.
          category: eventCategoryOf(a.kind) as EventCategory,
          weight: a.severity === 'CRITICAL' ? 3 : a.severity === 'WARN' ? 2 : 1,
        },
      })),
  }
}

/** '#RRGGBB' -> 'rgba(r,g,b,alpha)', for a heatmap-color ramp that fades from transparent
 *  up to a category's own hue. */
function withAlpha(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16)
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  return `rgba(${r},${g},${b},${alpha})`
}

const scoreColor: ExpressionSpecification = [
  'step', ['get', 'score'],
  TIER_COLOR.t0, 0.35, TIER_COLOR.t1, 0.55, TIER_COLOR.t2, 0.8, TIER_COLOR.t3,
] as unknown as ExpressionSpecification

/**
 * Only cells that are actually slow get drawn.
 *
 * Rendering every cell meant 440 green dots tracing the corridor to say "nothing is
 * wrong" -- the map spent all of its ink on normality and the two jams that mattered were
 * lost in it. An exceptions console shows exceptions; free-flowing road is the absence of
 * a marker, not a green one.
 *
 * quixapp/rules/congestion.py's first threshold is 0.35, so that is where a cell becomes
 * worth an operator's attention and where it starts being drawn.
 *
 * on_site cells (Dhar's plant/yard/gate/queue, Dhule's depot, ...) are excluded here too
 * -- trucks queueing or parked on-site score the same as a real jam (near-zero speed,
 * high stopped_ratio), and this layer answers "is the corridor moving," not "are trucks
 * dwelling at the plant." See isOnSiteCell's own comment for why the backend scores
 * these cells at all instead of just dropping them.
 */
const DEGRADED: ExpressionSpecification = [
  'all',
  ['>=', ['get', 'score'], 0.35],
  ['!=', ['get', 'on_site'], 1],
] as unknown as ExpressionSpecification

export function addHeatLayers(map: MlMap, before?: string): void {
  // MapLibre rejects a `before` that does not exist yet, and the throw escapes the
  // style.load handler -- taking every layer added after it with it. Tolerate a missing
  // anchor rather than making layer order a load-bearing invariant.
  const anchor = before && map.getLayer(before) ? before : undefined
  if (!map.getSource(SRC_CELLS)) map.addSource(SRC_CELLS, { type: 'geojson', data: empty })
  if (!map.getSource(SRC_EVENTS)) map.addSource(SRC_EVENTS, { type: 'geojson', data: empty })

  // A soft wash under the markers so a run of jammed cells reads as a corridor-length
  // problem at low zoom, where individual cells are a few pixels apart.
  if (!map.getLayer(LYR_CELL_HEAT)) {
    map.addLayer({
      id: LYR_CELL_HEAT,
      type: 'heatmap',
      source: SRC_CELLS,
      filter: DEGRADED,
      maxzoom: 13,
      paint: {
        'heatmap-weight': ['interpolate', ['linear'], ['get', 'score'], 0, 0, 1, 1],
        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 6, 0.6, 13, 1.4],
        'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 6, 14, 13, 44],
        'heatmap-opacity': 0.45,
        'heatmap-color': [
          'interpolate', ['linear'], ['heatmap-density'],
          0, 'rgba(0,0,0,0)',
          0.25, 'rgba(62,156,120,0.35)',
          0.5, 'rgba(216,167,46,0.5)',
          0.75, 'rgba(210,96,58,0.65)',
          1, 'rgba(168,43,28,0.8)',
        ],
      },
    }, anchor)
  }

  if (!map.getLayer(LYR_CELL_DOT)) {
    map.addLayer({
      id: LYR_CELL_DOT,
      type: 'circle',
      source: SRC_CELLS,
      filter: DEGRADED,
      paint: {
        // Hollow while the baseline is provisional: an unlearned road must not be
        // claimed as a clear one.
        'circle-color': [
          'case', ['==', ['get', 'provisional'], 1], 'rgba(0,0,0,0)', scoreColor,
        ] as unknown as ExpressionSpecification,
        'circle-opacity': 0.55,
        'circle-stroke-color': scoreColor,
        'circle-stroke-width': ['case', ['==', ['get', 'provisional'], 1], 1.6, 0.8],
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          7, ['interpolate', ['linear'], ['get', 'vehicles'], 1, 4, 10, 9],
          14, ['interpolate', ['linear'], ['get', 'vehicles'], 1, 11, 10, 26],
        ],
      },
    }, anchor)
  }

  if (!map.getLayer(LYR_CELL_LABEL)) {
    map.addLayer({
      id: LYR_CELL_LABEL,
      type: 'symbol',
      source: SRC_CELLS,
      minzoom: 10,
      filter: ['>=', ['get', 'score'], 0.55],   // label only what is genuinely congested
      layout: {
        'text-field': [
          'concat', ['to-string', ['round', ['get', 'avg_speed_kph']]], ' kph',
        ] as unknown as ExpressionSpecification,
        'text-font': ['Noto Sans Regular'],
        'text-size': 9,
        'text-offset': [0, -1.4],
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#EEF0F7',
        'text-halo-color': '#07080F',
        'text-halo-width': 1.4,
      },
    }, anchor)
  }

  // One heatmap layer per event category, each filtered to its own kinds and ramped to
  // its own hue -- see EVENT_CATEGORIES's comment for why one blended kernel was wrong.
  for (const cat of EVENT_CATEGORIES) {
    const id = eventLayerId(cat.id)
    if (map.getLayer(id)) continue
    map.addLayer({
      id,
      type: 'heatmap',
      source: SRC_EVENTS,
      filter: ['==', ['get', 'category'], cat.id] as unknown as ExpressionSpecification,
      paint: {
        'heatmap-weight': ['interpolate', ['linear'], ['get', 'weight'], 1, 0.35, 3, 1],
        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 6, 0.8, 15, 2.2],
        'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 6, 12, 15, 50],
        'heatmap-opacity': 0.75,
        'heatmap-color': [
          'interpolate', ['linear'], ['heatmap-density'],
          0, withAlpha(cat.color, 0),
          0.3, withAlpha(cat.color, 0.35),
          0.65, withAlpha(cat.color, 0.65),
          1, withAlpha(cat.color, 0.9),
        ],
      },
    }, anchor)
  }
}

export function setHeatData(
  map: MlMap, cells: GeoJSON.FeatureCollection<GeoJSON.Point>,
  events: GeoJSON.FeatureCollection<GeoJSON.Point>,
): void {
  const c = map.getSource(SRC_CELLS)
  if (c instanceof GeoJSONSource) c.setData(cells)
  const e = map.getSource(SRC_EVENTS)
  if (e instanceof GeoJSONSource) e.setData(events)
}

const CONGESTION_LAYERS = [LYR_CELL_HEAT, LYR_CELL_DOT, LYR_CELL_LABEL]

export function setHeatMode(map: MlMap, mode: HeatMode): void {
  for (const id of CONGESTION_LAYERS) {
    if (map.getLayer(id)) {
      map.setLayoutProperty(id, 'visibility', mode === 'congestion' ? 'visible' : 'none')
    }
  }
  for (const cat of EVENT_CATEGORIES) {
    const id = eventLayerId(cat.id)
    if (map.getLayer(id)) {
      map.setLayoutProperty(id, 'visibility', mode === 'events' ? 'visible' : 'none')
    }
  }
}
